import { forwardRef, type InputHTMLAttributes, type ReactNode } from 'react';
import { useFieldError } from '../a11y/useFieldError';
import styles from './Checkbox.module.css';

export interface CheckboxProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'id'> {
  /** Visible label, wired to the input via `htmlFor`. A required state must be
   * conveyed in this text (never colour/asterisk alone). */
  label: ReactNode;
  /** Explicit id; auto-generated when omitted. */
  id?: string;
  /** Field-level error. Sets `aria-invalid` and points `aria-describedby` at the
   * rendered error node. */
  error?: string;
}

/**
 * Accessible checkbox: a native `<input type="checkbox">` with a visible
 * `<label htmlFor>`, so Space toggles it natively (no custom key handler) and
 * assistive tech reports its role/state. `:focus-visible` uses the shared focus
 * ring; the label spans the 44px touch target. An `error` wires `aria-invalid`
 * and `aria-describedby` via {@link useFieldError}.
 */
export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(
  { label, id, error, className, ...rest },
  ref,
) {
  const hasError = Boolean(error);
  const { id: fieldId, errorId, controlProps } = useFieldError({
    ...(id ? { id } : {}),
    hasError,
    hasHelp: false,
  });

  const inputClasses = [styles.input, className ?? ''].filter(Boolean).join(' ');

  return (
    <div className={styles.field}>
      <div className={styles.row}>
        <input ref={ref} type="checkbox" className={inputClasses} {...rest} {...controlProps} />
        <label htmlFor={fieldId} className={styles.label}>
          {label}
        </label>
      </div>
      {hasError ? (
        <span id={errorId} className={styles.error}>
          {error}
        </span>
      ) : null}
    </div>
  );
});
